import * as React from "react";
import { useList } from "@refinedev/core";
import type { CrudFilter } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { CurateLocalityModal } from "@/components/CurateLocalityModal";

import type { AdminLocality, LocalityKind } from "@/pages/localities/types";
import { activeFrames, isCurated } from "@/pages/localities/types";

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type CurationFilter = "all" | "curated" | "uncurated";

export function LocalityList(): React.JSX.Element {
  const [kind, setKind] = React.useState<LocalityKind | "all">("country");
  const [curation, setCuration] = React.useState<CurationFilter>("uncurated");
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState<AdminLocality | null>(null);
  const debouncedSearch = useDebounced(search, 300);

  const filters: CrudFilter[] = [];
  if (kind !== "all") filters.push({ field: "kind", operator: "eq", value: kind });
  if (curation !== "all") {
    filters.push({ field: "curated", operator: "eq", value: curation === "curated" });
  }
  if (debouncedSearch.trim().length > 0) {
    filters.push({ field: "q", operator: "contains", value: debouncedSearch.trim() });
  }

  const { query } = useList<AdminLocality>({ resource: "localities", filters });
  const rows = query.data?.data ?? [];

  const showEmpty = !query.isLoading && !query.isError && rows.length === 0;

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-[20px] font-[600]">Картинки локаций</h1>
        <p className="text-[13px] text-[var(--muted)]">
          Кураторская картинка перекрывает автоматически собранную. Локации без курирования
          показаны первыми.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Тип локации"
          className="h-9 rounded-[var(--radius-sm)] border border-[var(--border)] px-2 text-[13px]"
          value={kind}
          onChange={(event) => setKind(event.target.value as LocalityKind | "all")}
        >
          <option value="country">Страны</option>
          <option value="city">Города</option>
          <option value="all">Все</option>
        </select>

        <select
          aria-label="Состояние курирования"
          className="h-9 rounded-[var(--radius-sm)] border border-[var(--border)] px-2 text-[13px]"
          value={curation}
          onChange={(event) => setCuration(event.target.value as CurationFilter)}
        >
          <option value="uncurated">Без курирования</option>
          <option value="curated">С курированием</option>
          <option value="all">Все</option>
        </select>

        <input
          aria-label="Поиск по названию"
          className="h-9 min-w-[220px] flex-1 rounded-[var(--radius-sm)] border border-[var(--border)] px-3 text-[13px]"
          placeholder="Поиск по названию"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {query.isError ? (
        <ErrorBanner message="Не удалось загрузить локации" onRetry={() => void query.refetch()} />
      ) : null}
      {query.isLoading ? <SkeletonTable rows={8} /> : null}
      {showEmpty ? (
        <EmptyState message="Ничего не найдено — измените фильтры или строку поиска" />
      ) : null}

      {rows.length > 0 ? (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
          {rows.map((locality) => {
            const visible = activeFrames(locality.frames);
            const lead = visible[0];
            return (
              <li key={`${locality.kind}:${locality.id}`} className="rounded-[var(--radius)] border border-[var(--border)] overflow-hidden">
                <div className="h-[120px] bg-[var(--surface-2)]">
                  {lead !== undefined ? (
                    <img
                      src={lead.thumbnailRef ?? lead.displayRef}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[12px] text-[var(--muted)]">
                      нет картинки
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[14px] font-[500]">{locality.name}</span>
                    <span className="text-[11px] text-[var(--muted)]">
                      {locality.countryIsoCode ?? "—"}
                    </span>
                  </div>
                  <span className="text-[12px] text-[var(--muted)]">
                    {isCurated(locality) ? "кураторская" : "автоматическая"} · кадров:{" "}
                    {visible.length}
                  </span>
                  <button
                    type="button"
                    className="mt-2 h-8 rounded-[var(--radius-sm)] border border-[var(--border)] text-[13px]"
                    onClick={() => setEditing(locality)}
                  >
                    Подобрать картинку
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {editing !== null ? (
        <CurateLocalityModal
          locality={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void query.refetch();
          }}
        />
      ) : null}
    </section>
  );
}
